import { type ActorView, getPosts, type PostResult, type PostView } from "../../queries.ts";
import { type ImagesEmbed, Like, Post, type PostEmbed, Repost } from "../../structs.ts";
import { formatError, publicTransport, RpcClient } from "../api.ts";
import { type AuthenticatedState, authState } from "../auth.ts";
import { requestCompose } from "../compose.ts";
import { blobUrl } from "../cdn.ts";
import { config } from "../config.ts";
import { embeddedImages, mediaWarning } from "../embed.ts";
import { getEagerPost, removeEagerPost } from "../eager-posts.ts";
import { cacheActor, cachePost } from "../view-cache.ts";
import { filterChanges, hiddenState, isFiltered, setHidden, threadRoot } from "../filters.ts";
import { postInteractions } from "../interactions.ts";
import { canWriteCollection } from "../permissions.ts";
import { accentColor } from "../channels.ts";
import { deletePost, rkeyOf } from "../repo.ts";
import { postPath, postPathFromUri, profilePath } from "../router.ts";
import { fullDate } from "../timestamps.ts";
import { actorHandle, actorLabel, Avatar } from "./actor.tsx";
import { dropdown, menuItem as item } from "./menu.tsx";
import { confirmDialog } from "./confirm.tsx";
import { moderationItems } from "./moderation.tsx";
import { AbsoluteTime, RelativeTime } from "./post-time.tsx";
import { RichText, safeExternalUrl } from "./rich-text.tsx";

const appView = new RpcClient(publicTransport(config.appView));

/** Feed rows note replies; thread rows sit under their context; the focal post is
 * expanded; notification rows carry the reason as their context line. */
export type PostVariant = "feed" | "thread" | "focal" | "notification";

export function ActorLink({ actor }: { actor: ActorView }): Element {
  cacheActor(actor);
  return (
    <a class="actor-link" href={profilePath(actor.handle || actor.did)} dataset={{ route: "" }}>
      {Avatar(actor)}
      <span class="actor-identity">
        <strong>{actorLabel(actor)}</strong>
        <span>{actorHandle(actor)}</span>
      </span>
    </a>
  );
}

/** Compact, link-free rendering for quote embeds and composer previews. */
export function PostSnippet(post: PostView): Element {
  cachePost(post);
  const date = post.record.createdAt ? new Date(post.record.createdAt) : undefined;
  return (
    <div class="post-snippet">
      <div class="post-snippet-header">
        {Avatar(post.author)}
        <strong>{actorLabel(post.author)}</strong>
        <span>{actorHandle(post.author)}</span>
        {date && RelativeTime(date)}
      </div>
      <p>{post.record.text}</p>
      {postImages(post)}
    </div>
  );
}

/**
 * The post a notification is about, as a quiet excerpt: its text, or what it carries, or why it's
 * gone. Filtered posts vanish.
 */
export function NotificationSubject(result: PostResult): Element | undefined {
  const resolved = resolvePost(result);
  if (!("post" in resolved)) {
    return <p class="notification-subject unavailable">This post is unavailable.</p>;
  }
  const { post } = resolved;
  if (isFiltered(post)) return undefined;
  cachePost(post);
  const images = embeddedImages(post.record.embed).length;
  const text = post.record.text ||
    (images ? `${images.toLocaleString()} ${images === 1 ? "image" : "images"}` : "");
  return text ? <p class="notification-subject">{text}</p> : undefined;
}

/** A result's post, or why it can't be shown; a just-published local copy stands in for notFound. */
function resolvePost(result: PostResult): { post: PostView } | { reason: string } {
  if (result.$type === "blue.cerulea.app.views#availablePost") return { post: result.post };
  const eager = result.reason === "notFound" ? getEagerPost(result.uri) : undefined;
  return eager ? { post: eager } : { reason: result.reason };
}

/** Image grid shared by full posts and quote snippets; nothing without a CDN. */
export function postImages(post: PostView): Element | undefined {
  const images = embeddedImages(post.record.embed);
  const cdn = config.cdn;
  if (!images.length || !cdn) return undefined;
  const warning = mediaWarning(post.record.labels);
  // Previews sit inside links, where a reveal control would be invalid; the post itself has one.
  if (warning) {
    return <span class="embed media-placeholder">Content warning: {warning} · images hidden</span>;
  }
  const grid = <div class="embed-images"></div>;
  for (const item of embedImages(images, post.author.did, cdn)) grid.append(item);
  return grid.children.length ? grid : undefined;
}

function embedImages(
  images: ImagesEmbed["images"],
  authorDid: string,
  cdn: URL,
): HTMLImageElement[] {
  const rendered: HTMLImageElement[] = [];
  for (const item of images) {
    const src = blobUrl(cdn, authorDid, item.image.ref.$link);
    if (!src) continue;
    const image = (
      <img class="embed-image" src={src} alt={item.alt ?? ""} loading="lazy" />
    ) as HTMLImageElement;
    if (item.aspectRatio) {
      image.width = item.aspectRatio.width;
      image.height = item.aspectRatio.height;
    }
    rendered.push(image);
  }
  return rendered;
}

/**
 * Make a whole card follow a route when clicked or activated from the keyboard, while its own
 * links and controls, and text selections, keep working.
 */
export function clickable(card: HTMLElement, href: string, label: string): void {
  const link = <a href={href} dataset={{ route: "" }} hidden>{label}</a> as HTMLAnchorElement;
  card.append(link);
  card.classList.add("clickable");
  card.tabIndex = 0;
  card.setAttribute("role", "link");
  card.setAttribute("aria-label", label);
  card.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : undefined;
    if (
      target?.closest("a, button, input, textarea, select, label, summary") ||
      getSelection()?.toString()
    ) {
      return;
    }
    link.click();
  });
  card.addEventListener("keydown", (event) => {
    if (event.target === card && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      link.click();
    }
  });
}

export interface PostCardProps {
  post: PostView;
  signal: AbortSignal;
  variant?: PostVariant;
  /** Show the post even when its author is muted (their own profile page). */
  showMuted?: boolean;
  /** Shown as a warning icon with a tooltip, e.g. when a strong reference's CID no longer matches. */
  notice?: string | undefined;
  /** Reason line in the reply-marker slot, e.g. who triggered this notification. */
  context?: string | undefined;
}

export function PostCard(props: PostCardProps): Element {
  const { post, signal, variant = "feed", notice, showMuted = false, context } = props;
  cachePost(post);
  const url = postPath(post.author, rkeyOf(post.uri));
  const date = post.record.createdAt ? new Date(post.record.createdAt) : undefined;
  const edited = post.record.editedAt ? new Date(post.record.editedAt) : undefined;
  const body = <p class="post-text"></p> as HTMLElement;
  body.append(RichText({ text: post.record.text, facets: post.record.facets }));
  const channel = post.channel;
  const accent = channel && accentColor(channel.record.accentColor);
  let side: Element | undefined;
  if (channel) {
    if (accent) {
      const bar = (
        <span class="channel-accent">
          <span class="channel-accent-label">{channel.record.name}</span>
        </span>
      ) as HTMLElement;
      bar.style.backgroundColor = accent;
      side = bar;
    } else {
      side = <span class="channel-plain">{channel.record.name}</span>;
    }
  }
  const actions = PostActions(post, signal, variant === "focal");
  const article = (
    <article class={`post-card ${variant === "focal" ? "post-focal" : ""}`}>
      <div class="post-main">
        <header class="post-header">
          {ActorLink({ actor: post.author })}
          <a class="post-time" href={url} dataset={{ route: "" }}>
            {!date ? "Post" : variant === "focal" ? AbsoluteTime(date) : RelativeTime(date, signal)}
          </a>
          {edited && <span class="post-edited" title={`Edited ${fullDate(edited)}`}>edited</span>}
          {notice && (
            <span class="post-notice" role="img" aria-label={notice}>
              <span class="lucide lucide-alert" aria-hidden="true"></span>
              <span class="post-notice-tip">{notice}</span>
            </span>
          )}
          {PostMenu(props, () => article)}
        </header>
        {variant === "notification" && context
          ? <span class="reply-marker">{context}</span>
          : variant === "feed" && post.record.reply
          ? ReplyMarker(post)
          : undefined}
        {body}
        {post.record.embed &&
          renderEmbed(post.record.embed, post.author.did, signal, mediaWarning(post.record.labels))}
        {variant === "focal" ? side : actions}
      </div>
      {variant === "focal" ? actions : side}
    </article>
  ) as HTMLElement;
  if (variant !== "focal") clickable(article, url, `View post by ${actorLabel(post.author)}`);
  if (variant !== "focal") foldTall(body, url);
  // Lists drop filtered posts on load; this handles ones hidden or muted while already on screen.
  if (variant !== "focal") {
    filterChanges.addEventListener("change", () => {
      if (isFiltered(post, showMuted)) article.remove();
    }, { signal });
  }
  return article;
}

function PostMenu(props: PostCardProps, card: () => HTMLElement): Element {
  const { post, signal, variant = "feed" } = props;
  const trigger = (
    <button class="icon-button post-menu-button" type="button" aria-label="Post options">
      <span class="lucide lucide-more" aria-hidden="true"></span>
    </button>
  ) as HTMLButtonElement;
  const menu = <div class="menu menu-right" role="menu"></div> as HTMLElement;
  const fill = () => {
    const hidden = hiddenState(post);
    const state = authState.get();
    const own = state.status === "authenticated" && state.did === post.author.did;
    menu.replaceChildren(
      item(
        hidden === "post" ? "Unhide post" : "Hide post",
        () => setHidden("post", post.uri, hidden !== "post"),
      ),
      item(
        hidden === "thread" ? "Unhide thread" : "Hide thread",
        () => setHidden("thread", threadRoot(post), hidden !== "thread"),
      ),
    );
    menu.append(...moderationItems(post.author));
    if (own && canWriteCollection(state.scopes, Post.id, "update")) {
      menu.append(item("Edit post", () =>
        requestCompose({
          kind: "edit",
          post,
          onSaved: (updated) => card().replaceWith(PostCard({ ...props, post: updated })),
        })));
    }
    if (own && canWriteCollection(state.scopes, Post.id, "delete")) {
      menu.append(
        item("Delete post", () => {
          void confirmDialog("Delete this post? This cannot be undone.", "Delete").then(
            (confirmed) => {
              if (!confirmed) return;
              void deletePost(post, signal).then(() => {
                removeEagerPost(post.uri);
                if (variant === "focal") {
                  card().replaceWith(
                    <article class="post-card unavailable-post">
                      <p>This post was deleted.</p>
                    </article>,
                  );
                } else card().remove();
              }).catch((error) => alert(formatError(error)));
            },
          );
        }, "danger-action"),
      );
    }
  };
  trigger.addEventListener("click", fill);
  dropdown(trigger, menu, signal);
  return <span class="menu-anchor">{trigger}{menu}</span>;
}

function ReplyMarker(post: PostView): Element {
  const parent = post.record.reply!.parent.uri;
  const to = post.replyTo;
  const label = to ? (to.handle ? `@${to.handle}` : actorLabel(to)) : "a post";
  const path = to ? postPath(to, rkeyOf(parent)) : postPathFromUri(parent);
  return path
    ? <a class="reply-marker" href={path} dataset={{ route: "" }}>Reply to {label}</a>
    : <span class="reply-marker">Reply to {label}</span>;
}

function PostActions(post: PostView, signal: AbortSignal, withStats: boolean): Element {
  const url = postPath(post.author, rkeyOf(post.uri));
  const reply = actionButton("Reply", "reply", post.replyCount);
  const repost = actionButton("Repost", "repost", post.repostCount);
  const like = actionButton("Like", "heart", post.likeCount);
  const repostItem = <button type="button" role="menuitem">Repost</button> as HTMLButtonElement;
  const quoteItem = <button type="button" role="menuitem">Quote post</button> as HTMLButtonElement;
  const menu = <div class="menu" role="menu">{repostItem}{quoteItem}</div> as HTMLElement;
  const stats = withStats ? <div class="post-stats"></div> : undefined;
  const status = <span class="interaction-status" role="status"></span>;
  let repository: ReturnType<typeof postInteractions> | undefined;
  let state: AuthenticatedState | undefined;
  let active = { like: false, repost: false };
  let counts = { like: post.likeCount, repost: post.repostCount };
  let session = new AbortController();

  const render = (kind: "like" | "repost", next: boolean, initial = false) => {
    if (!initial && active[kind] !== next) {
      counts[kind] = Math.max(0, counts[kind] + (next ? 1 : -1));
    }
    if (initial && next) counts[kind] = Math.max(1, counts[kind]);
    active[kind] = next;
    const button = kind === "like" ? like : repost;
    button.classList.toggle("active", next);
    button.ariaPressed = String(next);
    button.querySelector(".action-count")!.textContent = counts[kind]
      ? counts[kind].toLocaleString()
      : "";
    button.setAttribute("aria-label", `${next ? "Undo " : ""}${kind} (${counts[kind]})`);
    button.title = `${next ? "Undo " : ""}${kind[0]!.toUpperCase()}${kind.slice(1)}`;
    if (kind === "repost") repostItem.textContent = next ? "Undo repost" : "Repost";
    stats?.replaceChildren(
      ...([
        [counts.repost, "repost", "reposts"],
        [post.quoteCount, "quote", "quotes"],
        [counts.like, "like", "likes"],
      ] as const).filter(([count]) => count > 0).map(([count, one, many]) => (
        <a href={`${url}/${many}`} dataset={{ route: "" }}>
          <strong>{count.toLocaleString()}</strong> {count === 1 ? one : many}
        </a>
      )),
    );
  };

  const updateEnabled = () => {
    const can = (collection: string, action: "create" | "delete") =>
      !!state && canWriteCollection(state.scopes, collection, action);
    reply.disabled = !can(Post.id, "create");
    like.disabled = !can(Like.id, active.like ? "delete" : "create");
    repostItem.disabled = !can(Repost.id, active.repost ? "delete" : "create");
    quoteItem.disabled = !can(Post.id, "create");
    repost.disabled = repostItem.disabled && quoteItem.disabled;
  };

  const toggle = (kind: "like" | "repost") => {
    if (!repository) return;
    (kind === "like" ? like : repostItem).disabled = true;
    status.textContent = "";
    void repository.toggle(kind, post).then((next) => render(kind, next)).catch((error) => {
      status.textContent = formatError(error);
    }).finally(updateEnabled);
  };

  dropdown(repost, menu, signal);
  reply.addEventListener("click", () => requestCompose({ kind: "reply", post }));
  quoteItem.addEventListener("click", () => requestCompose({ kind: "quote", post }));
  repostItem.addEventListener("click", () => toggle("repost"));
  like.addEventListener("click", () => toggle("like"));

  const subscription = authState.subscribeImmediate((next) => {
    session.abort();
    session = new AbortController();
    state = next.status === "authenticated" ? next : undefined;
    repository = state ? postInteractions(state) : undefined;
    active = { like: false, repost: false };
    counts = { like: post.likeCount, repost: post.repostCount };
    render("like", false, true);
    render("repost", false, true);
    updateEnabled();
    if (!repository) return;
    like.disabled = repostItem.disabled = true;
    const current = repository;
    current.addEventListener("change", (event) => {
      const detail = (event as CustomEvent<{
        subject: string;
        kind: "like" | "repost";
        active: boolean;
      }>).detail;
      if (detail.subject === post.uri) {
        render(detail.kind, detail.active);
        updateEnabled();
      }
    }, { signal: session.signal });
    void current.get(post.uri).then((loaded) => {
      if (session.signal.aborted) return;
      render("like", loaded.like, true);
      render("repost", loaded.repost, true);
      updateEnabled();
    }).catch((error) => {
      if (!session.signal.aborted) status.textContent = formatError(error);
    });
  });
  signal.addEventListener("abort", () => {
    subscription.unsubscribe();
    session.abort();
  }, { once: true });

  return (
    <footer class="post-actions">
      {stats}
      <div class="post-action-buttons">
        {reply}
        <span class="menu-anchor">{repost}{menu}</span>
        {like}
      </div>
      {status}
    </footer>
  );
}

function actionButton(label: string, icon: string, count: number): HTMLButtonElement {
  return (
    <button
      class={`post-action action-${label.toLowerCase()}`}
      type="button"
      aria-label={`${label} (${count})`}
      title={label}
    >
      <span class="action-icon">
        <span class={`lucide lucide-${icon}`} aria-hidden="true"></span>
      </span>
      <span class="action-count">{count ? count.toLocaleString() : ""}</span>
    </button>
  ) as HTMLButtonElement;
}

export function PostResultView(
  { result, ...props }: Omit<PostCardProps, "post"> & { result: PostResult },
): Element | undefined {
  const resolved = resolvePost(result);
  if ("post" in resolved) {
    const { post } = resolved;
    return isFiltered(post, props.showMuted) ? undefined : PostCard({ ...props, post });
  }
  return (
    <article class="post-card unavailable-post">
      <p>This post is unavailable ({resolved.reason}).</p>
    </article>
  );
}

function renderEmbed(
  embed: PostEmbed,
  authorDid: string,
  signal: AbortSignal,
  warning: string | undefined,
): Element {
  switch (embed.$type) {
    case "blue.cerulea.app.embed.images": {
      const cdn = config.cdn;
      if (!cdn || !embed.images.length) {
        return (
          <div class="embed media-placeholder" role="note">
            {embed.images.length.toLocaleString()} {embed.images.length === 1 ? "image" : "images"}
            {" "}
            · media preview unavailable
          </div>
        );
      }
      const grid = <div class="embed-images"></div>;
      for (const image of embedImages(embed.images, authorDid, cdn)) grid.append(image);
      return warning
        ? (
          <details class="media-warning">
            <summary>Content warning: {warning}</summary>
            {grid}
          </details>
        )
        : grid;
    }
    case "blue.cerulea.app.embed.video":
      return <div class="embed media-placeholder" role="note">Video · preview unavailable</div>;
    case "blue.cerulea.app.embed.external": {
      const href = safeExternalUrl(embed.external.uri);
      return href
        ? (
          <a class="embed external" href={href} target="_blank" rel="noopener noreferrer">
            <strong>{embed.external.title || new URL(href).hostname}</strong>
            <span>{embed.external.description}</span>
            <small>{new URL(href).hostname}</small>
          </a>
        )
        : (
          <div class="embed external">
            <strong>{embed.external.title}</strong>
          </div>
        );
    }
    case "blue.cerulea.app.embed.record":
      return QuoteEmbed(embed.record, signal);
    case "blue.cerulea.app.embed.recordWithMedia": {
      const wrapper = <div class="embed-group"></div>;
      wrapper.append(
        QuoteEmbed(embed.record.record, signal),
        renderEmbed(embed.media, authorDid, signal, warning),
      );
      return wrapper;
    }
  }
}

function QuoteEmbed({ uri, cid }: { uri: string; cid: string }, signal: AbortSignal): Element {
  const path = postPathFromUri(uri);
  if (!path) return <div class="embed quote">Quoted record · preview unavailable</div>;
  const box = <a class="embed quote" href={path} dataset={{ route: "" }}>Loading quoted post…</a>;
  void appView.call(getPosts, { parameters: { uris: [uri as `at://${string}`] }, signal })
    .then(({ posts }) => {
      const result = posts[0];
      const available = result?.$type === "blue.cerulea.app.views#availablePost"
        ? result.post
        : undefined;
      const missing = !result ||
        (result.$type === "blue.cerulea.app.views#unavailablePost" &&
          result.reason === "notFound");
      const quoted = available ?? (missing ? getEagerPost(uri) : undefined);
      if (quoted) {
        box.setAttribute("href", postPath(quoted.author, rkeyOf(uri)));
        box.replaceChildren(PostSnippet(quoted));
        if (quoted.cid !== cid) {
          box.append(
            <span class="quote-notice">
              <span class="lucide lucide-alert" aria-hidden="true"></span>
              The quoted post was edited after it was quoted
            </span>,
          );
        }
      } else box.textContent = "Quoted post unavailable";
    }).catch(() => {
      if (!signal.aborted) box.textContent = "Quoted post · open to view";
    });
  return box;
}

const foldLines = 8;

/** Clip bodies taller than `foldLines` outside the focal view; the link opens the post expanded. */
function foldTall(body: HTMLElement, url: string): void {
  const more = (
    <a class="post-fold-more" href={url} dataset={{ route: "" }} hidden>
      Show more
    </a>
  ) as HTMLElement;
  body.after(more);
  // Cards are built detached, but callers append them synchronously, so the microtask sees layout.
  queueMicrotask(() => {
    const lineHeight = parseFloat(getComputedStyle(body).lineHeight);
    if (!(body.getBoundingClientRect().height > (foldLines + 0.5) * lineHeight)) return;
    // Defer writes so a page of cards costs one layout rather than one per card.
    requestAnimationFrame(() => {
      body.style.maxHeight = `${foldLines * lineHeight}px`;
      body.classList.add("post-fold");
      more.hidden = false;
    });
  });
}
